<template>
    <div class="flex justify-end">
        <div class="flex max-w-[80%] items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm" data-testid="file-upload-row">
            <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-500" aria-hidden="true">
                <!-- Image preview glyph -->
                <svg v-if="isImage" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <!-- Generic document glyph -->
                <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
            </span>
            <div class="flex min-w-0 flex-col leading-tight">
                <span class="truncate font-medium text-slate-800" :title="data.filename">{{ data.filename }}</span>
                <span class="text-xs text-slate-400">{{ kindLabel }} · {{ formattedSize }}</span>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import type { FileUploadData } from "./types";

const props = defineProps<{ data: FileUploadData }>();

const isImage = computed(() => props.data.mimetype.startsWith("image/"));

// Short, human-friendly label for the file kind, derived from the MIME type.
const KIND_LABELS: Record<string, string> = {
    "application/pdf": "PDF",
    "text/plain": "Text",
    "text/csv": "CSV",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "Word",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "Excel",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation": "PowerPoint",
};

const kindLabel = computed(() => {
    if (KIND_LABELS[props.data.mimetype]) {
        return KIND_LABELS[props.data.mimetype];
    }
    if (isImage.value) {
        return "Image";
    }
    return "File";
});

const formattedSize = computed(() => {
    const bytes = props.data.file_size;
    if (bytes < 1024) {
        return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(0)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
});
</script>
